// LoginDesign06 — Neumorphic soft UI
// Pale grey-blue surface, raised card, inset inputs, gentle twin shadows, calm blue button.
// Built with shadcn-style primitives (./ui/shadcn) + framer-motion. All login logic is identical to Login.js.
import CopyrightIcon from "@mui/icons-material/Copyright";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Dialog, DialogContent, Grow } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion } from "./ui/useReducedMotion";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../context/AuthContext";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LaptopIcon from "@mui/icons-material/Laptop";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TabletIcon from "@mui/icons-material/Tablet";
import DevicesIcon from "@mui/icons-material/Devices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import {
  ThemeScope,
  Button,
  Input,
  Label,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  Badge,
  Separator,
} from "./ui/shadcn";

const MotionCard = motion.create(Card);
const MotionButton = motion.create(Button);

// shadcn tokens for the neumorphic concept (+ a few extra neu tokens)
const THEME = {
  "--background": "#e6ebf2",
  "--foreground": "#1f2b40",
  "--card": "#e6ebf2",
  "--card-foreground": "#1f2b40",
  "--primary": "#3562c8",
  "--primary-foreground": "#ffffff",
  "--secondary": "#e6ebf2",
  "--secondary-foreground": "#2f58b8",
  "--muted": "#e6ebf2",
  "--muted-foreground": "#55657f",
  "--accent": "#e6ebf2",
  "--destructive": "#b42318",
  "--destructive-foreground": "#ffffff",
  "--border": "transparent",
  "--input": "transparent",
  "--ring": "#3f6fd8",
  "--radius": "1.1rem",
  "--neu-dark": "#c2c9d8",
  "--neu-light": "#ffffff",
  "--neu-blue": "#3f6fd8",
  "--neu-blue-deep": "#2f58b8",
  "--neu-ink-soft": "#4a5a75",
  "--neu-amber": "#b45309",
  "--neu-field": "#dfe5ee",
  "--neu-edge": "#7d8ca8",
  "--neu-ph": "#4d5d78",
};

const SHADOW_REST = "6px 6px 14px #c2c9d8, -6px -6px 14px #ffffff";
const SHADOW_SWELL = "10px 10px 22px #c2c9d8, -10px -10px 22px #ffffff";

const CSS = [
  "@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');",
  `
.login06-root.ui-scope{font-family:'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif;-webkit-font-smoothing:antialiased}
.login06-root .ui-btn,.login06-root .ui-input,.login06-root .ui-label{font-family:inherit}

/* ---------- page ---------- */
.login06-root.n6-page{min-height:100vh;width:100%;max-width:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;position:relative;overflow-x:hidden;background:var(--background);padding:max(20px,env(safe-area-inset-top)) max(16px,env(safe-area-inset-right)) max(16px,env(safe-area-inset-bottom)) max(16px,env(safe-area-inset-left))}
@supports (height:100dvh){.login06-root.n6-page{min-height:100dvh}}
.n6-orb{position:absolute;z-index:0;border-radius:50%;background:var(--background);pointer-events:none}
.n6-orb-a{width:200px;height:200px;top:-70px;left:-70px;box-shadow:12px 12px 28px var(--neu-dark),-12px -12px 28px var(--neu-light)}
.n6-orb-b{width:240px;height:240px;bottom:-90px;right:-70px;box-shadow:inset 12px 12px 26px var(--neu-dark),inset -12px -12px 26px var(--neu-light)}
.n6-orb-c{display:none;width:84px;height:84px;top:14%;right:9%;box-shadow:8px 8px 18px var(--neu-dark),-8px -8px 18px var(--neu-light)}
.n6-main{position:relative;z-index:1;width:100%;min-width:0;display:flex;justify-content:center}

/* ---------- shell ---------- */
.login06-root .n6-shell{position:relative;width:100%;max-width:460px;display:grid;grid-template-columns:minmax(0,1fr);min-width:0;border:0;border-radius:28px;background:var(--background);box-shadow:8px 8px 18px var(--neu-dark),-8px -8px 18px var(--neu-light)}
.n6-panel{display:flex;align-items:center;justify-content:center;padding:32px 22px 26px;min-width:0}
.n6-view{width:100%;min-width:0;max-width:400px}
.n6-brand{display:none}
.n6-mobile-logo{display:flex;justify-content:center;margin-bottom:6px}

/* ---------- logo plate ---------- */
.n6-plate{display:inline-flex;align-items:center;justify-content:center;padding:12px 22px;border-radius:20px;background:var(--background);box-shadow:6px 6px 14px var(--neu-dark),-6px -6px 14px var(--neu-light)}
.n6-plate img{display:block;height:36px;width:auto;max-width:100%}

/* ---------- brand column ---------- */
.n6-eyebrow.ui-badge{background:var(--background);color:var(--neu-blue-deep);border:0;padding:9px 16px;font-size:.7rem;font-weight:800;letter-spacing:.14em;text-transform:uppercase;box-shadow:inset 3px 3px 6px var(--neu-dark),inset -3px -3px 6px var(--neu-light)}
.n6-hero{margin:20px 0 14px;overflow-wrap:break-word;font-size:clamp(1.9rem,3vw,2.75rem);font-weight:800;line-height:1.12;letter-spacing:-.02em;color:var(--foreground)}
.n6-hero span{background:linear-gradient(90deg,#2f62d3,#4a82ee);-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;color:transparent}
.n6-lede{margin:0;max-width:420px;font-size:.95rem;line-height:1.8;color:var(--neu-ink-soft)}
.n6-feats{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:16px}
.n6-feat{display:flex;align-items:center;gap:14px;font-size:.92rem;font-weight:700;color:var(--neu-ink-soft)}
.n6-tick{flex:0 0 auto;width:32px;height:32px;border-radius:50%;display:grid;place-items:center;background:var(--background);color:var(--neu-blue);box-shadow:4px 4px 8px var(--neu-dark),-4px -4px 8px var(--neu-light)}

/* ---------- card parts ---------- */
.login06-root .ui-card-header{padding:0 0 26px;gap:8px}
.login06-root .ui-card-title{overflow-wrap:break-word;font-size:clamp(1.55rem,5.5vw,2rem);font-weight:800;line-height:1.15;letter-spacing:-.02em;color:var(--foreground)}
.login06-root .ui-card-description{overflow-wrap:break-word;font-size:clamp(.88rem,2.6vw,.95rem);line-height:1.55;color:var(--muted-foreground);margin-top:8px}
.login06-root .ui-card-content{padding:0}
.login06-root .ui-card-footer{padding:14px 0 0;justify-content:center;text-align:center;font-size:.9rem;color:var(--muted-foreground)}
.n6-signup{display:flex;flex-wrap:wrap;align-items:center;justify-content:center;column-gap:6px}

/* ---------- fields ---------- */
.n6-group{margin-bottom:6px}
.login06-root .ui-label{margin:0 0 10px 4px;font-size:.82rem;font-weight:800;letter-spacing:.02em;color:var(--neu-ink-soft)}
.n6-field{position:relative;display:flex;align-items:center}
.n6-lead{position:absolute;left:16px;top:50%;transform:translateY(-50%);display:flex;color:var(--muted-foreground);pointer-events:none;transition:color .25s ease}
.n6-field:focus-within .n6-lead{color:var(--neu-blue)}
/* Field = slightly darker well than the card + 1px edge + inset twin shadows, so it never blends into the card. */
.login06-root .ui-input{height:54px;border:1px solid var(--neu-edge);border-radius:16px;background:var(--neu-field);color:var(--foreground);-webkit-text-fill-color:var(--foreground);caret-color:var(--foreground);opacity:1;padding:0 46px 0 46px;min-width:0;font-size:1rem;font-weight:600;box-shadow:inset 5px 5px 10px var(--neu-dark),inset -5px -5px 10px var(--neu-light);transition:box-shadow .3s ease,border-color .25s ease}
.login06-root .ui-input::placeholder{color:var(--neu-ph);-webkit-text-fill-color:var(--neu-ph);opacity:1;font-weight:500}
.login06-root .ui-input:hover{border-color:#5f6f8c;box-shadow:inset 6px 6px 12px var(--neu-dark),inset -6px -6px 12px var(--neu-light)}
.login06-root .ui-input:focus-visible{border-color:var(--neu-blue);box-shadow:inset 6px 6px 12px var(--neu-dark),inset -6px -6px 12px var(--neu-light),0 0 0 2px var(--neu-blue)}
.login06-root .ui-input:-webkit-autofill,.login06-root .ui-input:-webkit-autofill:hover{-webkit-text-fill-color:var(--foreground);caret-color:var(--foreground);-webkit-box-shadow:inset 5px 5px 10px var(--neu-dark),inset -5px -5px 10px var(--neu-light),inset 0 0 0 1000px var(--neu-field);box-shadow:inset 5px 5px 10px var(--neu-dark),inset -5px -5px 10px var(--neu-light),inset 0 0 0 1000px var(--neu-field);transition:background-color 99999s ease-out 0s}
.login06-root .ui-input:-webkit-autofill:focus,.login06-root .ui-input:-webkit-autofill:focus-visible{border-color:var(--neu-blue);-webkit-text-fill-color:var(--foreground);caret-color:var(--foreground);-webkit-box-shadow:0 0 0 2px var(--neu-blue),inset 6px 6px 12px var(--neu-dark),inset -6px -6px 12px var(--neu-light),inset 0 0 0 1000px var(--neu-field);box-shadow:0 0 0 2px var(--neu-blue),inset 6px 6px 12px var(--neu-dark),inset -6px -6px 12px var(--neu-light),inset 0 0 0 1000px var(--neu-field);transition:background-color 99999s ease-out 0s}
.login06-root .n6-input-pw{padding-right:98px}
.n6-end{position:absolute;right:5px;top:50%;transform:translateY(-50%);display:flex;align-items:center;gap:6px;pointer-events:none}
.n6-end>*{pointer-events:auto}
.n6-warn{display:flex;color:var(--neu-amber)}
.n6-end>.n6-warn:last-child{margin-right:12px}
.login06-root .n6-eye.ui-btn{width:44px;height:44px;min-width:44px;padding:0;border-radius:50%;background:var(--background);color:var(--muted-foreground);box-shadow:3px 3px 7px var(--neu-dark),-3px -3px 7px var(--neu-light)}
.login06-root .n6-eye.ui-btn:hover{background:var(--background);color:var(--neu-blue-deep)}
.login06-root .n6-eye.ui-btn:active{box-shadow:inset 3px 3px 6px var(--neu-dark),inset -3px -3px 6px var(--neu-light)}
.login06-root .n6-eye.ui-btn:focus-visible{box-shadow:0 0 0 2px var(--background),0 0 0 4px var(--neu-blue)}
.n6-eye-ico{display:flex}
.n6-hint{display:flex;align-items:center;gap:6px;min-height:20px;margin:6px 4px 0;font-size:.78rem;font-weight:700;color:var(--neu-amber)}
.n6-forgot{display:flex;justify-content:flex-end;margin:2px 0 14px}
.login06-root .n6-link{display:inline-flex;align-items:center;min-height:44px;color:var(--neu-blue-deep);font-weight:800;text-decoration:none;border-radius:8px;padding:0 6px;transition:color .2s ease}
.login06-root .n6-link:hover{text-decoration:underline;text-underline-offset:4px}
.login06-root .n6-link:focus-visible{outline:2px solid var(--neu-blue);outline-offset:2px}
.n6-forgot .n6-link{font-size:.84rem}

/* ---------- alert ---------- */
.n6-alert-wrap{margin-bottom:20px}
.login06-root .ui-alert{display:flex;align-items:center;gap:10px;border:0;border-radius:16px;background:var(--background);padding:13px 16px;font-size:.86rem;font-weight:700;line-height:1.4;box-shadow:inset 4px 4px 9px var(--neu-dark),inset -4px -4px 9px var(--neu-light)}
.login06-root .ui-alert svg{flex:0 0 auto}

/* ---------- buttons ---------- */
.login06-root .ui-btn{transition:box-shadow .25s ease,background .25s ease,color .2s ease,opacity .2s ease}
.login06-root .n6-primary.ui-btn{width:100%;height:54px;border:0;border-radius:16px;font-size:1rem;font-weight:800;letter-spacing:.01em;color:#fff;background:linear-gradient(145deg,#3f72dc 0%,#2f58b8 100%);box-shadow:7px 7px 16px rgba(47,88,184,.38),-6px -6px 14px var(--neu-light)}
.login06-root .n6-primary.ui-btn:hover{opacity:1;background:linear-gradient(145deg,#3a6bd4 0%,#2a51ab 100%);box-shadow:9px 9px 20px rgba(47,88,184,.45),-7px -7px 16px var(--neu-light)}
.login06-root .n6-primary.ui-btn:active{box-shadow:inset 5px 5px 12px rgba(20,45,120,.55),inset -4px -4px 10px rgba(130,170,255,.45)}
.login06-root .n6-primary.ui-btn:focus-visible{box-shadow:0 0 0 3px var(--background),0 0 0 6px var(--neu-blue),7px 7px 16px rgba(47,88,184,.38)}
.login06-root .n6-primary.ui-btn:disabled{opacity:.72;box-shadow:inset 4px 4px 9px rgba(20,45,120,.5),inset -3px -3px 8px rgba(130,170,255,.4)}
.n6-btn-inner{display:inline-flex;align-items:center;gap:10px}
.login06-root .n6-soft.ui-btn{width:100%;height:50px;min-height:44px;border:0;border-radius:16px;font-size:.95rem;font-weight:800;color:var(--neu-blue-deep);background:var(--background);box-shadow:6px 6px 13px var(--neu-dark),-6px -6px 13px var(--neu-light)}
.login06-root .n6-soft.ui-btn:hover{opacity:1;background:var(--background);box-shadow:8px 8px 17px var(--neu-dark),-8px -8px 17px var(--neu-light)}
.login06-root .n6-soft.ui-btn:active{box-shadow:inset 4px 4px 9px var(--neu-dark),inset -4px -4px 9px var(--neu-light)}
.login06-root .n6-soft.ui-btn:focus-visible{box-shadow:0 0 0 3px var(--background),0 0 0 6px var(--neu-blue)}
.n6-bio{margin-top:16px}
.login06-root .n6-danger.ui-btn{flex:0 0 auto;min-width:92px;height:44px;border:0;border-radius:12px;font-size:.78rem;font-weight:800;color:#fff;background:linear-gradient(145deg,#d9503f 0%,#b83227 100%);box-shadow:4px 4px 10px rgba(184,50,39,.3),-4px -4px 9px var(--neu-light)}
.login06-root .n6-danger.ui-btn:hover{opacity:1;box-shadow:5px 5px 12px rgba(184,50,39,.4),-5px -5px 11px var(--neu-light)}
.login06-root .n6-danger.ui-btn:active{box-shadow:inset 3px 3px 7px rgba(110,20,14,.5),inset -3px -3px 7px rgba(255,150,140,.35)}
.login06-root .n6-danger.ui-btn:focus-visible{box-shadow:0 0 0 2px var(--background),0 0 0 4px var(--destructive)}

/* ---------- separator ---------- */
.login06-root .n6-sep.ui-sep{height:4px;margin:26px 0 0;border-radius:4px;background:var(--background);box-shadow:inset 2px 2px 3px var(--neu-dark),inset -2px -2px 3px var(--neu-light)}

/* ---------- company selection ---------- */
.n6-list{display:flex;flex-direction:column;gap:14px;min-width:0;max-height:min(46vh,340px);overflow-y:auto;overflow-x:hidden;margin:0 -14px 18px;padding:12px 18px 18px 14px}
.login06-root .n6-company.ui-card{display:flex;align-items:center;gap:14px;min-width:0;min-height:60px;padding:14px 16px;border:0;border-radius:18px;cursor:pointer;outline:none;background:var(--background);box-shadow:6px 6px 13px var(--neu-dark),-6px -6px 13px var(--neu-light);transition:box-shadow .25s ease}
.login06-root .n6-company.ui-card:hover{box-shadow:8px 8px 17px var(--neu-dark),-8px -8px 17px var(--neu-light)}
.login06-root .n6-company.ui-card[aria-checked="true"]{box-shadow:inset 5px 5px 10px var(--neu-dark),inset -5px -5px 10px var(--neu-light),0 0 0 2px var(--neu-blue)}
.login06-root .n6-company.ui-card:focus-visible{outline:3px solid var(--neu-blue);outline-offset:3px}
.n6-ico{flex:0 0 auto;width:42px;height:42px;border-radius:50%;display:grid;place-items:center;color:var(--neu-blue);background:var(--background);box-shadow:inset 3px 3px 6px var(--neu-dark),inset -3px -3px 6px var(--neu-light)}
.n6-company-name{flex:1;min-width:0;font-size:.95rem;font-weight:800;line-height:1.35;color:var(--foreground);overflow-wrap:anywhere;word-break:break-word}
.n6-radio{flex:0 0 auto;width:24px;height:24px;border-radius:50%;display:grid;place-items:center;background:var(--background);box-shadow:inset 3px 3px 5px var(--neu-dark),inset -3px -3px 5px var(--neu-light)}
.n6-radio-dot{width:16px;height:16px;border-radius:50%;display:grid;place-items:center;color:#fff;background:linear-gradient(145deg,#3f72dc,#2f58b8)}

/* ---------- footer ---------- */
.n6-foot{position:relative;z-index:1;margin-top:20px;max-width:100%;padding:0 8px;display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:4px;text-align:center;font-size:.76rem;font-weight:600;color:var(--muted-foreground)}

/* ---------- session dialog ---------- */
.n6-dialog{font-family:'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif;background:var(--background);color:var(--foreground)}
.n6-dhead{padding:24px 24px 8px}
.n6-dtop{display:flex;align-items:center;gap:14px;margin-bottom:16px;min-width:0}
.n6-dtop>div{min-width:0}
.n6-dico{flex:0 0 auto;width:48px;height:48px;border-radius:50%;display:grid;place-items:center;color:var(--neu-blue);background:var(--background);box-shadow:5px 5px 11px var(--neu-dark),-5px -5px 11px var(--neu-light)}
.n6-dtitle{margin:0;overflow-wrap:break-word;font-size:1.1rem;font-weight:800;line-height:1.3;color:var(--foreground)}
.n6-dsub{margin-top:6px}
.n6-dnote{margin:0;padding:12px 16px;border-radius:16px;font-size:.84rem;font-weight:600;line-height:1.5;color:var(--neu-ink-soft);box-shadow:inset 4px 4px 8px var(--neu-dark),inset -4px -4px 8px var(--neu-light)}
.login06-root .n6-dbadge.ui-badge{background:var(--background);color:var(--neu-blue-deep);border:0;padding:5px 12px;border-radius:14px;line-height:1.35;font-size:.72rem;font-weight:800;box-shadow:inset 2px 2px 4px var(--neu-dark),inset -2px -2px 4px var(--neu-light)}
.n6-dbody{padding:12px 20px 4px}
.n6-dlabel{margin:0 0 14px 4px;font-size:.72rem;font-weight:800;letter-spacing:.12em;text-transform:uppercase;color:var(--muted-foreground)}
.login06-root .n6-session.ui-card{display:flex;align-items:center;gap:12px;padding:12px 14px;margin:0 6px 16px;border:0;border-radius:18px;background:var(--background);box-shadow:5px 5px 11px var(--neu-dark),-5px -5px 11px var(--neu-light)}
.n6-sinfo{flex:1 1 120px;min-width:0}
.n6-sname{font-size:.88rem;font-weight:800;color:var(--foreground);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.n6-smeta{display:flex;flex-wrap:wrap;align-items:center;gap:2px 5px;margin-top:3px;font-size:.74rem;font-weight:600;color:var(--muted-foreground)}
.n6-dfoot{padding:4px 26px 24px}

/* ---------- responsive ---------- */
/* phones (<=479): tighter session dialog, stacked session actions */
@media (max-width:479px){
  .n6-dhead{padding:20px 18px 6px}
  .n6-dbody{padding:10px 12px 2px}
  .n6-dfoot{padding:2px 18px 20px}
  .login06-root .n6-session.ui-card{flex-wrap:wrap;margin:0 4px 14px}
  .login06-root .n6-danger.ui-btn{flex:1 1 100%}
}
@media (max-width:359px){
  .login06-root.n6-page{padding-left:max(12px,env(safe-area-inset-left));padding-right:max(12px,env(safe-area-inset-right))}
  .n6-panel{padding:26px 16px 22px}
  .login06-root .n6-input-pw{padding-right:92px}
  .login06-root .ui-input{padding-left:42px}
  .n6-lead{left:13px}
}
/* tablets / large phones (>=600) */
@media (min-width:600px){
  .login06-root.n6-page{padding-top:max(32px,env(safe-area-inset-top));padding-bottom:max(20px,env(safe-area-inset-bottom));padding-left:max(24px,env(safe-area-inset-left));padding-right:max(24px,env(safe-area-inset-right))}
  .login06-root .n6-shell{max-width:520px;border-radius:34px;box-shadow:14px 14px 32px var(--neu-dark),-14px -14px 32px var(--neu-light)}
  .n6-panel{padding:clamp(36px,5vw,48px) clamp(32px,6vw,48px) clamp(30px,4vw,40px)}
  .n6-orb-a{width:280px;height:280px;top:-90px;left:-90px}
  .n6-orb-b{width:320px;height:320px;bottom:-120px;right:-90px}
}
@media (min-width:900px){ .n6-orb-c{display:block} }
/* desktop / tablet landscape (>=1024): brand column + form side by side */
@media (min-width:1024px){
  .login06-root.n6-page{padding-top:max(40px,env(safe-area-inset-top));padding-bottom:max(24px,env(safe-area-inset-bottom));padding-left:max(32px,env(safe-area-inset-left));padding-right:max(32px,env(safe-area-inset-right))}
  .login06-root .n6-shell{max-width:1060px;grid-template-columns:minmax(0,1.05fr) minmax(0,1fr);border-radius:42px;box-shadow:18px 18px 40px var(--neu-dark),-18px -18px 40px var(--neu-light)}
  .n6-brand{display:flex;flex-direction:column;justify-content:center;gap:clamp(28px,4vh,44px);min-width:0;position:relative;padding:clamp(40px,6vh,60px) clamp(32px,4vw,56px)}
  .n6-brand::after{content:"";pointer-events:none;position:absolute;right:0;top:12%;bottom:12%;width:4px;border-radius:4px;box-shadow:inset 2px 2px 3px var(--neu-dark),inset -2px -2px 3px var(--neu-light)}
  .n6-panel{padding:clamp(40px,6vh,56px) clamp(32px,4vw,52px) clamp(32px,5vh,44px)}
  .n6-mobile-logo{display:none}
  .n6-plate img{height:42px}
  .n6-orb-a{width:340px;height:340px}
  .n6-orb-b{width:380px;height:380px}
}
/* large desktops / 1920 */
@media (min-width:1600px){
  .login06-root .n6-shell{max-width:1180px}
  .n6-view{max-width:420px}
  .n6-hero{font-size:3rem}
  .n6-lede{font-size:1rem;max-width:460px}
  .n6-feat{font-size:.98rem}
  .n6-orb-a{width:420px;height:420px}
  .n6-orb-b{width:460px;height:460px}
  .n6-orb-c{width:110px;height:110px}
}
/* short viewports (landscape phones, e.g. 640x360): compact the form, let the page scroll */
@media (max-height:560px){
  .login06-root.n6-page{padding-top:max(12px,env(safe-area-inset-top));padding-bottom:max(10px,env(safe-area-inset-bottom))}
  .login06-root .n6-shell{border-radius:26px}
  .n6-panel{padding:22px clamp(20px,5vw,32px) 18px}
  .login06-root .ui-card-header{padding-bottom:14px;gap:4px}
  .login06-root .ui-card-title{font-size:1.45rem}
  .login06-root .ui-card-description{margin-top:4px}
  .n6-mobile-logo{margin-bottom:2px}
  .n6-mobile-logo .n6-plate{padding:7px 16px;border-radius:14px}
  .n6-mobile-logo .n6-plate img{height:26px}
  .login06-root .ui-label{margin-bottom:6px}
  .n6-hint{min-height:16px;margin-top:3px}
  .n6-forgot{margin-bottom:8px}
  .login06-root .n6-sep.ui-sep{margin-top:14px}
  .login06-root .ui-card-footer{padding-top:4px}
  .n6-foot{margin-top:10px}
  .n6-list{max-height:none;overflow:visible}
  .n6-brand{padding-top:28px;padding-bottom:28px;gap:24px}
  .n6-hero{margin:12px 0 10px}
  .n6-feats{gap:10px}
  .login06-root .ui-input{height:48px}
  .login06-root .n6-primary.ui-btn{height:48px}
  .n6-orb-c{display:none}
}
@supports (height:100dvh){
  .n6-list{max-height:min(46dvh,340px)}
}
@media (max-height:560px){ .n6-list{max-height:none !important} }
@media (prefers-reduced-motion:reduce){
  .login06-root *,.login06-root *::before,.login06-root *::after{transition-duration:.01ms !important;animation-duration:.01ms !important}
}
`,
].join("\n");

const EASE = [0.22, 1, 0.36, 1];
const INSTANT = { duration: 0 };

function LoginDesign06({ connectionString }) {
  const path = connectionString;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reduce = useReducedMotion();

  const { login } = useAuth();

  const autoIdentifier = searchParams.get("identifier");
  const autoUsername = searchParams.get("UserName");
  const autoMobile = searchParams.get("mobile");
  const autoEmail = searchParams.get("email");
  const autoPassword = searchParams.get("password");
  const redirectPathFromQuery = searchParams.get("redirect");

  const [identifier, setIdentifier] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanySelectionVisible, setIsCompanySelectionVisible] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Session limit picker
  const [sessionPickerData, setSessionPickerData] = useState(null);
  const [kickingSessionId, setKickingSessionId] = useState(null);

  // Device fingerprint (created once, persisted in localStorage)
  const getDeviceId = () => {
    let did = localStorage.getItem("_did");
    if (!did) {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        did = window.crypto.randomUUID();
      } else {
        // Fallback for HTTP (non-localhost) — crypto.randomUUID requires HTTPS
        did = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
      }
      localStorage.setItem("_did", did);
    }
    return did;
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome") && !ua.includes("Edg")) return `Chrome on ${getOSName()}`;
    if (ua.includes("Firefox")) return `Firefox on ${getOSName()}`;
    if (ua.includes("Safari") && !ua.includes("Chrome")) return `Safari on ${getOSName()}`;
    if (ua.includes("Edg")) return `Edge on ${getOSName()}`;
    return `Browser on ${getOSName()}`;
  };

  const getOSName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Windows")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    return "Unknown OS";
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return "just now";
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const deviceIcon = (type) => {
    if (type === "mobile")  return <PhoneAndroidIcon sx={{ fontSize: 20, color: "#3f6fd8" }} />;
    if (type === "tablet")  return <TabletIcon sx={{ fontSize: 20, color: "#3f6fd8" }} />;
    if (type === "desktop") return <DesktopWindowsIcon sx={{ fontSize: 20, color: "#3f6fd8" }} />;
    return <LaptopIcon sx={{ fontSize: 20, color: "#3f6fd8" }} />;
  };

  const handleTogglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s?.trim());

  useEffect(() => {
    const handleMessage = (ev) => {
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (!data || !data.type) return;
        if (data.type === "BIOMETRIC_LOGIN") {
          FN_HandleLogin(data.identifier, data.password);
        }
        if (data.type === "BIOMETRIC_FAILED") {
          setErrorMessage("Biometric authentication failed or cancelled.");
        }
        if (data.type === "BIOMETRIC_NOT_AVAILABLE") {
          setErrorMessage("Biometrics not available on this device.");
        }
      } catch (err) {
        console.warn("Error parsing message from native:", err);
      }
    };
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  const isLikelyMobile = (s) => {
    if (!s) return false;
    const cleaned = s.replace(/[\s\-\(\)\+]/g, "");
    const digits = cleaned.replace(/\D/g, "");
    const nonDigitCount = cleaned.replace(/\d/g, "").length;
    return (digits.length >= 6 && digits.length <= 15 && nonDigitCount === 0);
  };

  const determineIdentifierType = (id) => {
    const trimmed = id?.trim() || "";
    if (isEmail(trimmed)) return "email";
    if (isLikelyMobile(trimmed)) return "mobile";
    return "UserName";
  };

  useEffect(() => {
    const ai = autoIdentifier || autoUsername || autoMobile || autoEmail;
    if (ai && autoPassword) {
      setIsAutoLogin(true);
      setIdentifier(ai);
      setUserPassword(autoPassword);
      FN_HandleLogin(ai, autoPassword);
    }
  }, [autoIdentifier, autoUsername, autoMobile, autoEmail, autoPassword]);

  const FN_HandleLogin = async (overrideIdentifier, overridePassword, kickSessionId = null) => {
    setErrorMessage("");
    setIsLoading(true);
    const id = (overrideIdentifier ?? identifier ?? "").trim();
    const pwd = overridePassword ?? userPassword;

    if (!id || !pwd) {
      setErrorMessage("Please provide identifier and password.");
      setIsLoading(false);
      return;
    }

    try {
      const identifierType = determineIdentifierType(id);

      // Build device info
      let deviceInfo = {
        deviceId:   getDeviceId(),
        deviceName: getBrowserName(),
        platform:   navigator.platform,
        deviceType: "web",
      };
      if (window.electronAPI) {
        try { deviceInfo = await window.electronAPI.getDeviceInfo(); } catch (_) {}
      }

      const payload = {
        identifier: id,
        password: pwd,
        type: identifierType,
        deviceId:     deviceInfo.deviceId,
        deviceName:   deviceInfo.deviceName,
        platform:     deviceInfo.platform,
        deviceType:   deviceInfo.deviceType,
        kickSessionId: kickSessionId,
      };
      const response = await axios.post(`${path}/api/login`, payload,{ withCredentials: true });

      // Session limit reached — show device picker
      if (response.data.sessionLimitReached) {
        setSessionPickerData(response.data);
        setIsLoading(false);
        return;
      }

      if (!response.data.success) {
        setErrorMessage(response.data.message || "Invalid credentials.");
        return;
      }

      // Store session token for all future API requests
      if (response.data.sessionToken) {
        sessionStorage.setItem("sessionToken", response.data.sessionToken);
        axios.defaults.headers.common["x-session-token"] = response.data.sessionToken;
      }

      if (!overrideIdentifier && window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "STORE_CREDENTIALS", identifier: id, password: pwd })
        );
      }

      const user = response.data.user;
      const companyRes = await axios.get(`${path}/api/companies?companyId=${user.CompId}`);

      if (!companyRes.data.success || !companyRes.data.companies?.length) {
        navigate("/registerCompany", { replace: true, state: { pendingUser: user, password: pwd } });
        return;
      }

      const currentLoginCompanyList = companyRes.data.companies;
      const actualSelectedCompany =
        currentLoginCompanyList.find((c) => c.companyId === user.companyId || c.companyId === user.CompId) ||
        currentLoginCompanyList[0];

      const allRes = await axios.get(`${path}/api/All-companies`, { params: { userId: user.UserId } });
      const allCompanies =
        allRes.data.success && allRes.data.data?.length ? allRes.data.data : currentLoginCompanyList;
      const serverIsSuperAdmin = Boolean(allRes.data.isSuperAdmin);

      await login(user, allCompanies, actualSelectedCompany, serverIsSuperAdmin);

      setCompanies(currentLoginCompanyList);
      setSelectedCompany(actualSelectedCompany);

      if (isAutoLogin || currentLoginCompanyList.length === 1) {
        FN_SelectCompany(actualSelectedCompany);
      } else {
        setIsCompanySelectionVisible(true);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Login failed.");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const FN_SelectCompany = (companyOverride = null) => {
    const company = companyOverride || selectedCompany;
    if (!company) {
      setErrorMessage("Please select a company.");
      return;
    }
    localStorage.setItem("selectedCompanyId", company.CompId);
    const redirectPath = redirectPathFromQuery || "/main/home";
    navigate(redirectPath);
  };

  const handleCapsLockCheck = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState("CapsLock"));
    }
  };

  // ── Motion presets (all collapse to instant when the user prefers reduced motion) ──
  const instant = INSTANT;
  // Memoised so the variant objects keep a stable identity across re-renders (typing, caps-lock, etc.)
  const { viewVariants, itemVariants, fieldVariants, listVariants } = useMemo(() => ({
    viewVariants: {
      hidden: { opacity: 1 },
      show: {
        opacity: 1,
        transition: reduce ? INSTANT : { duration: 0.3, staggerChildren: 0.07, delayChildren: 0.05 },
      },
      exit: reduce ? { opacity: 0, transition: INSTANT } : { opacity: 0, x: -24, transition: { duration: 0.22, ease: "easeIn" } },
    },
    itemVariants: {
      hidden: reduce ? { opacity: 1 } : { opacity: 1, y: 16 },
      show: { opacity: 1, y: 0, transition: reduce ? INSTANT : { duration: 0.55, ease: EASE } },
    },
    // Input groups never fade through opacity 0 — they only slide in, so they can never be left invisible.
    fieldVariants: {
      hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 1, y: 14 },
      show: { opacity: 1, y: 0, transition: reduce ? INSTANT : { duration: 0.55, ease: EASE } },
    },
    listVariants: {
      hidden: {},
      show: { transition: reduce ? INSTANT : { staggerChildren: 0.06 } },
    },
  }), [reduce]);
  const hoverLift = reduce ? undefined : { y: -2 };
  const tapPress = reduce ? undefined : { scale: 0.985 };

  const features = [
    "Multi-company ledger management",
    "GST-compliant invoicing & filings",
    "Real-time cash flow analytics",
    "Role-based access & audit trail",
  ];

  const capsHint = (id) => (
    <p id={id} className="n6-hint" aria-live="polite">
      {capsLockOn ? (
        <>
          <WarningAmberIcon sx={{ fontSize: 15 }} />
          Caps Lock is ON
        </>
      ) : (
        " "
      )}
    </p>
  );

  const errorAlert = (
    <AnimatePresence>
      {errorMessage && (
        <motion.div
          key={errorMessage}
          className="n6-alert-wrap"
          initial={reduce ? false : { opacity: 1, y: -10 }}
          animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, x: [0, -8, 8, -5, 5, 0] }}
          exit={{ opacity: 0, transition: { duration: 0.15 } }}
          transition={reduce ? instant : { opacity: { duration: 0.25 }, y: { duration: 0.25 }, x: { duration: 0.45, ease: "easeOut" } }}
        >
          <Alert variant="destructive">
            <WarningAmberIcon sx={{ fontSize: 19 }} />
            <span>{errorMessage}</span>
          </Alert>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <ThemeScope className="login06-root n6-page" vars={THEME}>
      <style>{CSS}</style>

      {/* Ambient soft shapes */}
      <motion.div
        className="n6-orb n6-orb-a"
        aria-hidden="true"
        animate={reduce ? undefined : { y: [0, -22, 0], x: [0, 10, 0] }}
        transition={{ duration: 0.01, repeat: 0, ease: "easeInOut" }}
      />
      <motion.div
        className="n6-orb n6-orb-b"
        aria-hidden="true"
        animate={reduce ? undefined : { y: [0, 20, 0], x: [0, -12, 0] }}
        transition={{ duration: 0.01, repeat: 0, ease: "easeInOut" }}
      />
      <motion.div
        className="n6-orb n6-orb-c"
        aria-hidden="true"
        animate={reduce ? undefined : { y: [0, -14, 0] }}
        transition={{ duration: 0.01, repeat: 0, ease: "easeInOut" }}
      />

      <main className="n6-main">
        <MotionCard
          className="n6-shell"
          initial={reduce ? false : { opacity: 1, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={reduce ? instant : { duration: 0.7, ease: EASE }}
        >
          {/* ── BRAND COLUMN (desktop) ── */}
          <aside className="n6-brand">
            <motion.div
              className="n6-plate"
              style={{ alignSelf: "flex-start" }}
              initial={reduce ? false : { opacity: 1, y: 14 }}
              animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, boxShadow: [SHADOW_REST, SHADOW_SWELL, SHADOW_REST] }}
              transition={reduce ? instant : {
                opacity: { duration: 0.6, delay: 0.2 },
                y: { duration: 0.6, delay: 0.2, ease: EASE },
                boxShadow: { duration: 0.01, repeat: 0, ease: "easeInOut" },
              }}
            >
              <img src="/RBLOGOBLUE.svg" alt="ReonBook" />
            </motion.div>

            <motion.div variants={viewVariants} initial={reduce ? false : "hidden"} animate="show">
              <motion.div variants={itemVariants}>
                <Badge variant="secondary" className="n6-eyebrow">Enterprise Accounting Platform</Badge>
              </motion.div>
              <motion.p variants={itemVariants} className="n6-hero">
                Financial Intelligence<br />
                <span>for Modern Business</span>
              </motion.p>
              <motion.p variants={itemVariants} className="n6-lede">
                Complete suite to manage invoices, GST compliance, cash flow, and multi-company ledgers — all in one place.
              </motion.p>
            </motion.div>

            <motion.ul
              className="n6-feats"
              variants={{ hidden: {}, show: { transition: reduce ? instant : { staggerChildren: 0.1, delayChildren: 0.45 } } }}
              initial={reduce ? false : "hidden"}
              animate="show"
            >
              {features.map((feat) => (
                <motion.li
                  key={feat}
                  className="n6-feat"
                  variants={{ hidden: reduce ? { opacity: 1 } : { opacity: 1, x: -18 }, show: { opacity: 1, x: 0, transition: reduce ? instant : { duration: 0.5, ease: EASE } } }}
                  whileHover={reduce ? undefined : { x: 4 }}
                >
                  <span className="n6-tick" aria-hidden="true"><CheckIcon sx={{ fontSize: 16 }} /></span>
                  <span>{feat}</span>
                </motion.li>
              ))}
            </motion.ul>
          </aside>

          {/* ── FORM COLUMN ── */}
          <section className="n6-panel">
            <AnimatePresence mode="wait">
              {!isCompanySelectionVisible ? (

                /* ── LOGIN FORM ── */
                <motion.div
                  key="login-view"
                  className="n6-view"
                  variants={viewVariants}
                  initial={reduce ? false : "hidden"}
                  animate="show"
                  exit="exit"
                >
                  <CardHeader>
                    <motion.div variants={itemVariants} className="n6-mobile-logo">
                      <div className="n6-plate"><img src="/RBLOGOBLUE.svg" alt="ReonBook" /></div>
                    </motion.div>
                    <motion.div variants={itemVariants}>
                      <CardTitle role="heading" aria-level={1}>Welcome</CardTitle>
                      <CardDescription>Sign in to access your ReonBook dashboard</CardDescription>
                    </motion.div>
                  </CardHeader>

                  <CardContent>
                    <form onSubmit={(e) => { e.preventDefault(); setIsAutoLogin(false); FN_HandleLogin(); }}>

                      <motion.div variants={fieldVariants} className="n6-group">
                        <Label htmlFor="login06-identifier">Username, Email or Mobile</Label>
                        <div className="n6-field">
                          <span className="n6-lead" aria-hidden="true"><PersonOutlineIcon sx={{ fontSize: 21 }} /></span>
                          <Input
                            id="login06-identifier"
                            type="text"
                            value={identifier}
                            placeholder="Enter username, email or mobile"
                            autoComplete="username"
                            autoCapitalize="none"
                            spellCheck={false}
                            aria-describedby="login06-identifier-hint"
                            onChange={(e) => setIdentifier(e.target.value)}
                            onKeyUp={handleCapsLockCheck}
                            onFocus={handleCapsLockCheck}
                            onBlur={() => setCapsLockOn(false)}
                          />
                          {capsLockOn && (
                            <span className="n6-end"><span className="n6-warn" aria-hidden="true"><WarningAmberIcon sx={{ fontSize: 20 }} /></span></span>
                          )}
                        </div>
                        {capsHint("login06-identifier-hint")}
                      </motion.div>

                      <motion.div variants={fieldVariants} className="n6-group">
                        <Label htmlFor="login06-password">Password</Label>
                        <div className="n6-field">
                          <span className="n6-lead" aria-hidden="true"><LockOutlinedIcon sx={{ fontSize: 21 }} /></span>
                          <Input
                            id="login06-password"
                            className="n6-input-pw"
                            type={showPassword ? "text" : "password"}
                            value={userPassword}
                            placeholder="Enter your password"
                            autoComplete="current-password"
                            aria-describedby="login06-password-hint"
                            onChange={(e) => setUserPassword(e.target.value)}
                            onKeyUp={handleCapsLockCheck}
                            onFocus={handleCapsLockCheck}
                            onBlur={() => setCapsLockOn(false)}
                          />
                          <span className="n6-end">
                            {capsLockOn && <span className="n6-warn" aria-hidden="true"><WarningAmberIcon sx={{ fontSize: 20 }} /></span>}
                            <MotionButton
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="n6-eye"
                              onClick={handleTogglePasswordVisibility}
                              aria-label={showPassword ? "Hide password" : "Show password"}
                              aria-pressed={showPassword}
                              whileTap={tapPress}
                            >
                              <AnimatePresence mode="wait" initial={false}>
                                <motion.span
                                  key={showPassword ? "hide" : "show"}
                                  className="n6-eye-ico"
                                  initial={reduce ? false : { opacity: 1, scale: 0.6, rotate: -60 }}
                                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                                  exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.6, rotate: 60 }}
                                  transition={reduce ? instant : { duration: 0.16 }}
                                >
                                  {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                                </motion.span>
                              </AnimatePresence>
                            </MotionButton>
                          </span>
                        </div>
                        {capsHint("login06-password-hint")}
                      </motion.div>

                      <motion.div variants={itemVariants} className="n6-forgot">
                        <Link to="/forgot-password" className="n6-link">Forgot password?</Link>
                      </motion.div>

                      {errorAlert}

                      <motion.div variants={itemVariants}>
                        <MotionButton
                          type="submit"
                          className="n6-primary"
                          disabled={isLoading}
                          whileHover={hoverLift}
                          whileTap={tapPress}
                        >
                          {isLoading ? (
                            <span className="n6-btn-inner" aria-live="polite">
                              <CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />
                              Signing in...
                            </span>
                          ) : (
                            "Sign In"
                          )}
                        </MotionButton>
                      </motion.div>

                      {typeof window !== "undefined" && window.isNativeApp && window.ReactNativeWebView && (
                        <MotionButton
                          type="button"
                          variant="secondary"
                          className="n6-soft n6-bio"
                          whileHover={hoverLift}
                          whileTap={tapPress}
                          onClick={() => window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "REQUEST_BIOMETRIC" }))}
                        >
                          Login with Biometric
                        </MotionButton>
                      )}
                    </form>

                    <motion.div variants={itemVariants}>
                      <Separator className="n6-sep" />
                    </motion.div>
                  </CardContent>

                  <motion.div variants={itemVariants}>
                    <CardFooter>
                      <span className="n6-signup">
                        <span>Don't have an account?</span>
                        <Link to="/register" className="n6-link">Sign up now</Link>
                      </span>
                    </CardFooter>
                  </motion.div>
                </motion.div>

              ) : (

                /* ── COMPANY SELECTION ── */
                <motion.div
                  key="company-view"
                  className="n6-view"
                  variants={viewVariants}
                  initial={reduce ? false : "hidden"}
                  animate="show"
                  exit="exit"
                >
                  <CardHeader>
                    <motion.div variants={itemVariants}>
                      <CardTitle id="login06-company-title" role="heading" aria-level={1}>Select Organization</CardTitle>
                      <CardDescription>Choose which organization to access</CardDescription>
                    </motion.div>
                  </CardHeader>

                  <CardContent>
                    <motion.div
                      className="n6-list"
                      role="radiogroup"
                      aria-labelledby="login06-company-title"
                      variants={listVariants}
                    >
                      {companies.map((c) => {
                        const isSelected = selectedCompany?.companyId === c.companyId;
                        const pick = () => setSelectedCompany(companies.find((cc) => cc.companyId === c.companyId));
                        return (
                          <MotionCard
                            key={c.companyId}
                            className="n6-company"
                            role="radio"
                            aria-checked={isSelected}
                            tabIndex={0}
                            variants={itemVariants}
                            whileHover={hoverLift}
                            whileTap={tapPress}
                            onClick={pick}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                pick();
                              }
                            }}
                          >
                            <span className="n6-ico" aria-hidden="true"><ApartmentIcon sx={{ fontSize: 22 }} /></span>
                            <span className="n6-company-name">{c.companyName}</span>
                            <span className="n6-radio" aria-hidden="true">
                              <motion.span
                                className="n6-radio-dot"
                                initial={false}
                                animate={{ scale: isSelected ? 1 : 0, opacity: isSelected ? 1 : 0 }}
                                transition={reduce ? instant : { type: "spring", stiffness: 420, damping: 24 }}
                              >
                                <CheckIcon sx={{ fontSize: 11 }} />
                              </motion.span>
                            </span>
                          </MotionCard>
                        );
                      })}
                    </motion.div>

                    {errorAlert}

                    <motion.div variants={itemVariants}>
                      <MotionButton
                        type="button"
                        className="n6-primary"
                        onClick={() => FN_SelectCompany()}
                        whileHover={hoverLift}
                        whileTap={tapPress}
                      >
                        <span className="n6-btn-inner">
                          Continue to Dashboard
                          <ArrowForwardRoundedIcon sx={{ fontSize: 20 }} />
                        </span>
                      </MotionButton>
                    </motion.div>
                  </CardContent>
                </motion.div>
              )}
            </AnimatePresence>
          </section>
        </MotionCard>
      </main>

      <motion.footer
        className="n6-foot"
        initial={reduce ? false : { opacity: 1 }}
        animate={{ opacity: 1 }}
        transition={reduce ? instant : { duration: 0.6, delay: 0.7 }}
      >
        <CopyrightIcon sx={{ fontSize: 13 }} aria-hidden="true" />
        <span>{new Date().getFullYear()} Powered by Soltrack Technologies Pvt Ltd</span>
      </motion.footer>

      {/* ── SESSION LIMIT PICKER MODAL ── */}
      <Dialog
        open={!!sessionPickerData}
        onClose={() => setSessionPickerData(null)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="login06-session-title"
        slots={{ transition: Grow }}
        transitionDuration={reduce ? 0 : { enter: 320, exit: 180 }}
        slotProps={{
          backdrop: { sx: { backdropFilter: "blur(4px)", bgcolor: "rgba(40,52,78,0.38)" } },
          paper: { sx: { m: { xs: 2, sm: 4 }, width: { xs: "calc(100% - 32px)", sm: "calc(100% - 64px)" }, maxWidth: { xs: "calc(100% - 32px)", sm: 444 }, maxHeight: "calc(100% - 32px)", borderRadius: "28px", overflow: "hidden", bgcolor: "#e6ebf2", backgroundImage: "none", boxShadow: "16px 16px 36px rgba(60,72,100,0.35), -10px -10px 28px rgba(255,255,255,0.35)" } },
        }}
      >
        <DialogContent sx={{ p: 0, bgcolor: "#e6ebf2" }}>
          <ThemeScope className="login06-root n6-dialog" vars={THEME}>
            {/* Header */}
            <div className="n6-dhead">
              <div className="n6-dtop">
                <span className="n6-dico" aria-hidden="true"><DevicesIcon sx={{ fontSize: 23 }} /></span>
                <div>
                  <h2 id="login06-session-title" className="n6-dtitle">Session Limit Reached</h2>
                  <Badge variant="secondary" className="n6-dbadge n6-dsub">
                    {sessionPickerData?.maxSessions} of {sessionPickerData?.maxSessions} allowed session{sessionPickerData?.maxSessions !== 1 ? "s" : ""} in use
                  </Badge>
                </div>
              </div>
              <p className="n6-dnote">Sign out of one of the devices below to log in on this device.</p>
            </div>

            {/* Device list */}
            <div className="n6-dbody">
              <p className="n6-dlabel">Active Sessions</p>

              {sessionPickerData?.activeSessions?.map((session, idx) => (
                <MotionCard
                  key={session.id}
                  className="n6-session"
                  initial={reduce ? false : { opacity: 1, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={reduce ? instant : { duration: 0.45, ease: EASE, delay: 0.1 + idx * 0.07 }}
                >
                  <span className="n6-ico" aria-hidden="true">{deviceIcon(session.device_type)}</span>

                  <div className="n6-sinfo">
                    <div className="n6-sname">{session.device_name || "Unknown Device"}</div>
                    <div className="n6-smeta">
                      <AccessTimeIcon sx={{ fontSize: 13 }} />
                      <span>{timeAgo(session.last_activity)}</span>
                      {session.ip_address && <span>· {session.ip_address}</span>}
                    </div>
                  </div>

                  <MotionButton
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="n6-danger"
                    disabled={kickingSessionId === session.id}
                    whileHover={hoverLift}
                    whileTap={tapPress}
                    onClick={async () => {
                      setKickingSessionId(session.id);
                      setSessionPickerData(null);
                      await FN_HandleLogin(identifier, userPassword, session.id);
                      setKickingSessionId(null);
                    }}
                  >
                    {kickingSessionId === session.id
                      ? <CircularProgress size={14} sx={{ color: "#fff" }} />
                      : "Sign Out"}
                  </MotionButton>
                </MotionCard>
              ))}
            </div>

            {/* Cancel */}
            <div className="n6-dfoot">
              <MotionButton
                type="button"
                variant="secondary"
                className="n6-soft"
                whileHover={hoverLift}
                whileTap={tapPress}
                onClick={() => setSessionPickerData(null)}
              >
                Cancel
              </MotionButton>
            </div>
          </ThemeScope>
        </DialogContent>
      </Dialog>
    </ThemeScope>
  );
}

export default LoginDesign06;
